import type { MetricRegistryEntry } from './metric-registry';
import type { GovernedMeasureIdentity } from './governed-measure-identity';

export const ASSIGNED_PEOPLE_MEASURE_IDENTITY: GovernedMeasureIdentity = {
  measuredDimension: 'assigned_people',
  valueForm: 'count',
  unitFamily: 'count',
  normalizationBasis: 'none',
};

export const GRAPH_COST_METRIC = 'fully_loaded_annual_cost_usd' as const;
export const GRAPH_KNOWN_ASSIGNED_PEOPLE_COUNT_METRIC = 'known_assigned_people_count' as const;
export const GRAPH_KNOWN_ASSIGNED_PEOPLE_METRIC = Object.freeze({
  metricKey: GRAPH_KNOWN_ASSIGNED_PEOPLE_COUNT_METRIC,
  label: 'Known assigned people',
  definition:
    'Distinct canonical PERSON identities assigned to selected POSITION nodes; unassigned positions are excluded.',
  unit: 'people',
  reconciliationRule: 'Count each canonical PERSON identity once across selected POSITION nodes.',
} as const);

/** Graph-derived counts and the unchanged activated graph-cost definition. */
export const ORG_GRAPH_READ_METRIC_REGISTRY_SEED: readonly MetricRegistryEntry[] =
  Object.freeze([
    Object.freeze({
      ...GRAPH_KNOWN_ASSIGNED_PEOPLE_METRIC,
      registryVersion: 'metric_registry_v1',
      registryLayer: 'core',
      frame: 'organization state as of runtime source_as_of',
      direction: 'context',
      temporalType: 'count',
      appliesTo: [],
      blendPolicy: { kind: 'never_blend' },
      aggregationRules: [],
      requiredEvidenceClasses: ['org_state_as_is', 'graph_org_bridge_reference'],
      benchmarkable: false,
      sourceOfTruthClass: 'derived',
      ownerModuleId: 'org-graph-cost-rollup',
      sourceBindingKeys: [GRAPH_KNOWN_ASSIGNED_PEOPLE_COUNT_METRIC],
      source:
        `apps/api/src/decision-modules/org-graph-read/org-graph-read.manifest.ts#metricKey:${GRAPH_KNOWN_ASSIGNED_PEOPLE_COUNT_METRIC}`,
      measureIdentity: ASSIGNED_PEOPLE_MEASURE_IDENTITY,
    } as const),
    Object.freeze({
      metricKey: GRAPH_COST_METRIC,
      registryVersion: 'metric_registry_v1',
      registryLayer: 'core',
      label: 'Fully loaded annual cost',
      definition:
        'Annual fully loaded cost of a named organization unit from the governed position-cost rollup.',
      unit: 'USD',
      frame: 'annual',
      direction: 'context',
      temporalType: 'flow',
      appliesTo: [],
      blendPolicy: { kind: 'never_blend' },
      aggregationRules: [],
      reconciliationRule:
        'Graph position-cost rollup reconciles to the authoritative position-cost roster for the named organization unit; the metric publishes only when both totals agree within $0.01.',
      requiredEvidenceClasses: [
        'org_state_as_is',
        'position_cost_roster',
        'graph_org_bridge_reference',
      ],
      benchmarkable: false,
      sourceOfTruthClass: 'derived',
      ownerModuleId: 'org-graph-cost-rollup',
      sourceBindingKeys: [GRAPH_COST_METRIC],
      source:
        `apps/api/src/decision-modules/org-graph-read/org-graph-read.manifest.ts#metricKey:${GRAPH_COST_METRIC}`,
      measureIdentity: {
        measuredDimension: 'labor_cost',
        valueForm: 'amount',
        unitFamily: 'currency',
        normalizationBasis: 'none',
      },
    } as const),
  ]);
